import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { History, Radio, CheckCircle2, XCircle, FileDown, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { formatTicketCode } from "../lib/ticketCode";

const STATUS = {
  waiting: { label: "En attente", cls: "bg-[#FFFBE6] text-[#9A7D00]" },
  called: { label: "Appelé", cls: "bg-brand/10 text-brand" },
  present: { label: "Terminé", cls: "bg-[#ECFBEF] text-[#0A7F2E]", icon: CheckCircle2 },
  absent: { label: "Annulé", cls: "bg-surface text-subtle", icon: XCircle },
};

function formatAmount(amount, currency = "eur") {
  const n = Number(amount || 0);
  if (n === 0) return "Gratuit";
  const sym = currency?.toLowerCase() === "eur" ? "€" : (currency || "").toUpperCase();
  return `${n.toFixed(2)} ${sym}`;
}

function apiBase() {
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
}

export default function MyTickets() {
  const [items, setItems] = useState(null);
  const [emailing, setEmailing] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tickets/history");
        setItems(data);
      } catch (err) {
        if (err.response?.status === 401) nav("/login");
        setItems([]);
      }
    })();
  }, [nav]);

  const downloadReceipt = (t) => {
    const token = localStorage.getItem("bypassgo_token");
    // Open in a new tab — the backend sends PDF with inline Content-Disposition
    const url = `${apiBase()}/tickets/${t.id}/receipt.pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("err"); return r.blob(); })
      .then((blob) => {
        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href;
        a.download = `recu-bypassgo-ticket-${t.number}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);
      })
      .catch(() => toast.error("Impossible de télécharger le reçu"));
  };

  const emailReceipt = async (t) => {
    setEmailing((s) => ({ ...s, [t.id]: true }));
    try {
      const { data } = await api.post(`/tickets/${t.id}/receipt/email`);
      toast.success(`Reçu envoyé à ${data.emailedTo}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible");
    } finally {
      setEmailing((s) => ({ ...s, [t.id]: false }));
    }
  };

  if (items === null) return <div className="container mx-auto py-16 text-center text-subtle">Chargement…</div>;

  const served = items.filter((t) => t.status === "present").length;
  const cancelled = items.filter((t) => t.status === "absent").length;
  const active = items.filter((t) => ["waiting", "called"].includes(t.status)).length;

  return (
    <div className="container mx-auto px-4 md:px-8 py-10 md:py-14 max-w-4xl">
      <p className="overline mb-2">Historique</p>
      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-ink">Mes tickets</h1>

      <div className="mt-8 grid grid-cols-3 border border-border" data-testid="stats-grid">
        <Stat label="Terminés" value={served} />
        <Stat label="En cours" value={active} border />
        <Stat label="Annulés" value={cancelled} border />
      </div>

      <div className="mt-10">
        {items.length === 0 ? (
          <div className="border border-dashed border-border p-12 text-center">
            <History className="mx-auto text-subtle" size={36} />
            <p className="mt-4 text-ink font-medium">Aucun ticket dans votre historique</p>
            <Link to="/search" className="mt-3 inline-block text-brand font-medium text-sm">Parcourir les établissements →</Link>
          </div>
        ) : (
          <div className="border border-border divide-y divide-border">
            {items.map((t) => {
              const s = STATUS[t.status] || STATUS.waiting;
              const isPaid = Number(t.amount || 0) > 0;
              const canDownload = isPaid; // receipts available for paid tickets regardless of status
              return (
                <div key={t.id} className="flex items-start gap-4 p-5" data-testid={`history-row-${t.id}`}>
                  <div className="font-mono text-2xl font-bold text-ink w-16 shrink-0">{formatTicketCode(t)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display font-bold text-ink truncate">{t.commerceName}</p>
                      {t.queueName && t.queueName !== "File principale" && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-ink text-white px-1.5 py-0.5">
                          {t.queueName}
                        </span>
                      )}
                      {t.type === "priority" && (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-brand uppercase tracking-wider">
                          <Radio size={11} /> Distance
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-subtle mt-0.5">
                      {t.commerceType}{t.commerceCity ? ` · ${t.commerceCity}` : ""} · {new Date(t.createdAt).toLocaleString("fr-FR")}
                    </p>
                    <p className="mt-1 text-sm font-medium text-ink" data-testid={`history-amount-${t.id}`}>
                      {formatAmount(t.amount, t.currency)}
                    </p>
                    {canDownload && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          onClick={() => downloadReceipt(t)}
                          size="sm"
                          variant="outline"
                          className="h-8 border border-ink text-ink hover:bg-ink hover:text-white rounded-md text-xs"
                          data-testid={`receipt-download-${t.id}`}
                        >
                          <FileDown size={12} className="mr-1" /> Télécharger le reçu
                        </Button>
                        <Button
                          onClick={() => emailReceipt(t)}
                          disabled={!!emailing[t.id]}
                          size="sm"
                          variant="outline"
                          className="h-8 border border-border rounded-md text-xs"
                          data-testid={`receipt-email-${t.id}`}
                        >
                          <Mail size={12} className="mr-1" /> {emailing[t.id] ? "…" : "Recevoir par email"}
                        </Button>
                      </div>
                    )}
                  </div>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-bold uppercase tracking-wider shrink-0 ${s.cls}`}>
                    {s.icon && <s.icon size={12} />} {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, border }) {
  return (
    <div className={`p-5 ${border ? "border-l border-border" : ""}`}>
      <p className="overline">{label}</p>
      <p className="mt-2 font-mono text-3xl font-bold text-ink">{value}</p>
    </div>
  );
}
